import React, { ReactNode } from "react";

import { defineMessages, IntlShape } from "react-intl";

import RoleEnum from "@mapmycustomers/shared/enum/Role";
import Plan from "@mapmycustomers/shared/types/Plan";
import Role from "@mapmycustomers/shared/types/Role";
import User from "@mapmycustomers/shared/types/User";
import { Button, ModalFunc, showModal } from "@mapmycustomers/ui";

import PlanItem from "@app/component/CustomFieldsLimitModal/PlanItem";
import { planNameToInfo } from "@app/component/CustomFieldsLimitModal/planMessages";
import { isOwner } from "@app/store/iam/util";
import Iam from "@app/types/Iam";
import { isPurchasableTier } from "@app/util/plans/findPlanTier";

import styles from "./RecordLimitAlertModal.module.scss";

const messages = defineMessages({
  close: {
    id: "recordLimitAlertModal.close",
    defaultMessage: "Close",
    description: "Ok button in record limit modal for reps",
  },
  description: {
    id: "recordLimitAlertModal.description",
    defaultMessage:
      "Your organization has reached the maximum of {count, number, ::.} {count, plural, one {record} other {records}} (companies, person, and deals) allowed on your <b>{planName}</b> plan. {canUpgrade, select, true {To add more records, upgrade your plan.} other {Contact your administrator to upgrade and add more records.}} ",
    description: "Warning description for org limit reached",
  },
  importsDescription: {
    id: "recordLimitAlertModal.description.import",
    defaultMessage:
      "Your organization has reached the maximum of {count, number, ::.} {count, plural, one {record} other {records}} (companies, person, and deals) allowed on your <b>{planName}</b> plan. {canUpgrade, select, true {You cannot import any new records until you upgrade.} other {Contact your administrator to upgrade and continue importing.}}",
    description: "Warning description for org limit reached",
  },
  ok: {
    id: "recordLimitAlertModal.ok",
    defaultMessage: "View Plans & Pricing",
    description: "Ok button in record limit modal",
  },
  title: {
    id: "recordLimitAlertModal.title",
    defaultMessage: "Record limit reached",
    description: "Warning title for org limit reached",
  },
  upgrade: {
    id: "recordLimitAlertModal.plans.upgrade",
    defaultMessage: "Upgrade",
  },
});

type DetailsArg = {
  currentPlan: Plan;
  currentRole: Role;
  isImport?: boolean;
  onHide?: () => void;
  plans: Plan[];
  usageLimits: Iam["usageLimits"];
  users: User[];
};

const showRecordLimitAlertModal = (
  intl: IntlShape,
  details: DetailsArg
): ReturnType<ModalFunc> | undefined => {
  const availablePlans = details.plans
    .filter(
      (plan) =>
        isPurchasableTier(plan) &&
        !plan.trialing &&
        plan.upsellOrder > details.currentPlan.upsellOrder &&
        (plan.recordsLimit === -1 ||
          plan.recordsLimit > (details.usageLimits.records.usage ?? 0)) &&
        plan.customFieldsLimit !== 0 // allows to filter out Road Warrior plans
    )
    .sort((a, b) => a.upsellOrder - b.upsellOrder);

  const canUpgrade = [RoleEnum.MANAGER, RoleEnum.OWNER].includes(details.currentRole.key);

  const owners = details.users.filter(isOwner);

  if (!availablePlans.length) {
    return;
  }

  return showModal({
    afterClose: () => details.onHide?.(),
    closable: true,
    content: (
      <div className={styles.content}>
        <div className={styles.description}>
          {intl.formatMessage(
            details.isImport ? messages.importsDescription : messages.description,
            {
              b: (text: ReactNode) => <b>{text}</b>,
              canUpgrade,
              count: details.usageLimits.records.limit,
              planName: details.currentPlan.name,
            }
          )}
        </div>
        {canUpgrade ? (
          <div className={styles.planList}>
            {availablePlans.map((plan) => {
              const info = planNameToInfo[plan.name as keyof typeof planNameToInfo];
              return (
                <PlanItem
                  extra={
                    info ? (
                      <Button href={info.link} size="small" target="_blank" type="primary">
                        {intl.formatMessage(messages.upgrade)}
                      </Button>
                    ) : undefined
                  }
                  intl={intl}
                  key={plan.id}
                  plan={plan}
                />
              );
            })}
          </div>
        ) : (
          <div className={styles.ownerList}>
            {owners.map((owner) => (
              <div key={owner.id}>
                {owner.fullName ? `${owner.fullName} ` : ""}
                <a href={`mailto:${owner.username}`}>{owner.username}</a>
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    okButtonProps: canUpgrade
      ? {
          href: "https://mapmycustomers.com/pricing/",
          target: "_blank",
        }
      : undefined,
    okText: intl.formatMessage(canUpgrade ? messages.ok : messages.close),
    onOk: () => details.onHide?.(),
    title: intl.formatMessage(messages.title),
    type: "warning",
    width: "32rem",
  });
};

export default showRecordLimitAlertModal;
